import { CompanyId } from "@custom-types/base-types";
import { createSelector } from "@reduxjs/toolkit";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import {
  CompanySettingsResponse,
  CompanySettingsResponseMap,
  UserBatchSettingsResponse,
} from "@store/settings/settings-slice-types";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { RootState } from "@store/store-helper";
import {
  currentUserSelector,
  loggedInUserSelector,
} from "@store/user/user-selector";
import { DEFAULT_WAYPOINT_COLOR } from "@constants/waypoint-colors";

/**
 * Get the user store settings for the selected user.
 */
export const userSettingsSelector = createSelector(
  [
    (state: RootState) => state.settings.user,
    loggedInUserSelector,
    currentUserSelector,
  ],
  (
    userSettings,
    loggedInUser,
    currentUser
  ): Partial<UserBatchSettingsResponse> => {
    const userId = loggedInUser?.id ?? currentUser?.id;
    if (!userId) {
      return {};
    }
    return userSettings.data[userId] ?? {};
  }
);

/**
 * Selects the user's custom waypoint color from the store.
 * Falls back to the default blue if the setting hasn't been saved yet.
 */
export const waypointColorSelector = createSelector(
  [(state: RootState) => state.settings.company, selectedSdbCompanyIdSelector],
  (companySettings, selectedCompanyId): string => {
    const companySetting = selectedCompanyId
      ? companySettings[selectedCompanyId]?.find(
          (item) => item.identifier === "waypoints_custom_color"
        )
      : undefined;
    const value: unknown = companySetting?.value;
    return typeof value === "string" && value.length > 0
      ? value
      : DEFAULT_WAYPOINT_COLOR;
  }
);

/**
 * Returns true if user settings are being fetched, otherwise false.
 */
export const isFetchingUserSettingsSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state.settings.user,
    (userSettings) => {
      return userSettings.requestState === "fetching";
    }
  );

/**
 * Get the current company settings.
 * @param companyId The ID of the company for which to get settings.
 */
export function companySettingsSelector(
  companyId: CompanyId
): (state: RootState) => CompanySettingsResponse[] {
  return createSelector(
    (state: RootState) => state.settings.company,
    (companySettings) => {
      return companySettings[companyId] ?? [];
    }
  );
}

/**
 * Get the company settings map.
 * This is a map of identifiers to CompanySettingsResponse.
 * @param companyId The ID of the company for which to get settings.
 */
export function companySettingsMapSelector(
  companyId: CompanyId
): (state: RootState) => CompanySettingsResponseMap {
  return createSelector(
    companySettingsSelector(companyId),
    (companySettings) => {
      return companySettings.reduce((map, setting) => {
        map[setting.identifier] = setting;
        return map;
      }, {} as CompanySettingsResponseMap);
    }
  );
}

/**
 * Get a company setting by its identifier.
 * @param companyId The ID of the company for which to get settings.
 * @param identifier The identifier of the company setting.
 * @returns The company setting or undefined if not found.
 */
export function companySettingByIdentifierSelector<
  Identifier extends SettingsApiTypes.SettingsIdentifierByScope<"COMPANY">,
>(
  companyId: CompanyId,
  identifier: Identifier
): (
  state: RootState
) => SettingsApiTypes.SettingsResponse<"COMPANY", Identifier> | undefined {
  return createSelector(
    companySettingsMapSelector(companyId),
    (companySettingsMap) => {
      // Make shallow copy so can be modified without touching the store.
      // Helpful e.g. when changing then sending it to the backend (which would then update the store).
      const setting = companySettingsMap[identifier];
      return setting
        ? ({ ...setting } as SettingsApiTypes.SettingsResponse<
            "COMPANY",
            Identifier
          >)
        : undefined;
    }
  );
}
